import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Loader2, Sparkles, Check, X, Send, Settings2, Wand2, Zap, CheckCheck, Search, MessageSquare, Pencil, ChevronDown, ChevronUp } from "lucide-react";

const Field = ({ label, hint, children }) => (
  <label className="block mb-3">
    <div className="text-[11px] uppercase tracking-wider font-mono text-cyan-300/80 mb-1">{label}</div>
    {children}
    {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
  </label>
);
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:border-cyan-400/50 outline-none";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }); } catch { return ""; } };

const isUnknown = (name, handle) => {
  const n = (name || "").trim();
  if (!n) return true;
  if (n === (handle || "").trim()) return true;
  if (/^ig:/i.test(n)) return true;
  return /^\+?[0-9][0-9\s\-()]{4,}$/.test(n);
};

function DraftCard({ d, value, onChange, onApprove, onSkip, onRenamed }) {
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(d.name || "");
  const unknown = isUnknown(d.name, d.handle);

  const toggleThread = async () => {
    if (thread) { setOpen((o) => !o); return; }
    setLoadingThread(true);
    try {
      const t = d.channel === "dm" ? await api.instagramThread(d.person_id) : await api.messageThread(d.person_id);
      setThread(t); setOpen(true);
    } catch { toast.error("Could not load the conversation"); }
    finally { setLoadingThread(false); }
  };
  const saveName = async () => {
    const v = name.trim();
    if (!v) return;
    try { await api.assistantRenameContact(d.person_id, v); d.name = v; setRenaming(false); onRenamed?.(d.person_id, v); toast.success("Contact renamed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not rename"); }
  };

  return (
    <div data-testid={`assistant-draft-${d.id}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        {renaming ? (
          <div className="flex items-center gap-1.5 flex-1">
            <input data-testid={`assistant-rename-input-${d.id}`} autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveName()} className="flex-1 bg-black/40 border border-cyan-400/40 rounded-lg px-2 py-1 text-sm text-zinc-100 outline-none" placeholder="Name this contact" />
            <button data-testid={`assistant-rename-save-${d.id}`} onClick={saveName} className="w-7 h-7 grid place-items-center rounded-lg bg-cyan-500/20 border border-cyan-400/40 text-cyan-200"><Check size={13} /></button>
            <button onClick={() => { setRenaming(false); setName(d.name || ""); }} className="w-7 h-7 grid place-items-center rounded-lg border border-white/10 text-zinc-500"><X size={13} /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <button data-testid={`assistant-rename-${d.id}`} onClick={() => setRenaming(true)} className="group inline-flex items-center gap-1.5 min-w-0" title="Rename contact">
              <span className={`text-sm font-semibold truncate ${unknown ? "text-amber-300" : "text-zinc-200"}`}>{d.name || "Unknown"}</span>
              <Pencil size={11} className="text-zinc-600 group-hover:text-cyan-300 shrink-0" />
            </button>
            {unknown && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/30 shrink-0">name me</span>}
          </div>
        )}
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${d.channel === "dm" ? "bg-pink-500/15 text-pink-300 border-pink-400/30" : "bg-cyan-500/15 text-cyan-300 border-cyan-400/30"}`}>{d.channel === "dm" ? "Instagram" : "iMessage"}</span>
          {d.last_at && <span className="text-[10px] text-zinc-600 font-mono">{fmtDate(d.last_at)}</span>}
        </div>
      </div>
      <div className="text-[10px] text-zinc-600 font-mono mb-1.5">{d.handle}</div>

      {d.incoming && <div className="text-[12px] text-zinc-400 mb-2 italic">“{d.incoming.replace(/^.*?:\s*/, "")}”</div>}

      <button data-testid={`assistant-view-chat-${d.id}`} onClick={toggleThread} className="text-[11px] font-mono text-cyan-300/80 hover:text-cyan-200 inline-flex items-center gap-1 mb-2">
        {loadingThread ? <Loader2 size={11} className="animate-spin" /> : <MessageSquare size={11} />} {open ? "Hide conversation" : "View conversation"} {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {open && thread && (
        <div data-testid={`assistant-thread-${d.id}`} className="rounded-lg border border-white/10 bg-black/30 p-2.5 mb-2 max-h-56 overflow-y-auto space-y-1.5">
          {(thread.messages || []).slice(-14).map((m, i) => (
            <div key={i} className={`flex ${m.is_from_me ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-[12px] ${m.is_from_me ? "bg-cyan-500/20 text-cyan-50" : "bg-white/10 text-zinc-200"}`}>{m.text}</div>
            </div>
          ))}
          {(thread.messages || []).length === 0 && <div className="text-[11px] text-zinc-600 text-center py-2">No earlier messages found.</div>}
        </div>
      )}

      <textarea data-testid={`assistant-draft-text-${d.id}`} className={`${inputCls} h-20 resize-none`} value={value} onChange={(e) => onChange(e.target.value)} />
      <div className="flex items-center gap-2 mt-2">
        <button data-testid={`assistant-send-${d.id}`} onClick={onApprove} className="text-xs font-tactical uppercase tracking-wider px-3 py-1.5 rounded-lg bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 inline-flex items-center gap-1.5"><Send size={12} /> Approve & send</button>
        <button data-testid={`assistant-skip-${d.id}`} onClick={onSkip} className="text-xs font-tactical uppercase tracking-wider px-3 py-1.5 rounded-lg border border-white/10 text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1.5"><X size={12} /> Skip</button>
      </div>
    </div>
  );
}

export default function AssistantDesk({ onTopUp }) {
  const [status, setStatus] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [edits, setEdits] = useState({});
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [form, setForm] = useState({ name: "", tone: "", signature: "", dos: "", donts: "", scope: "all" });
  const poll = useRef(null);

  const loadStatus = () => api.assistantStatus().then((s) => {
    setStatus(s);
    setForm({ name: s.name || "", tone: s.tone || "", signature: s.signature || "", dos: s.dos || "", donts: s.donts || "", scope: s.scope || "all" });
    if (!s.configured) setShowConfig(true);
  }).catch(() => {});
  const loadDrafts = () => api.assistantDrafts().then((d) => { setDrafts(d.drafts || []); }).catch(() => {});
  const [search, setSearch] = useState("");

  useEffect(() => { loadStatus(); loadDrafts(); api.assistantTiers().then((t) => setTiers(t.tiers || [])).catch(() => {}); }, []);

  const startPoll = () => {
    if (poll.current) clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const r = await api.assistantRunStatus();
        setRun(r);
        if (r.status !== "running") { clearInterval(poll.current); poll.current = null; loadDrafts(); loadStatus(); setBusy(false); }
      } catch { clearInterval(poll.current); poll.current = null; setBusy(false); }
    }, 2000);
  };
  useEffect(() => () => poll.current && clearInterval(poll.current), []);

  const save = async () => {
    setBusy(true);
    try { await api.assistantConfig(form); toast.success("Assistant saved"); setShowConfig(false); await loadStatus(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const activate = async (tier) => {
    setBusy(true);
    try { const r = await api.assistantActivate(tier); toast.success(r.message); await loadStatus(); }
    catch (e) {
      const msg = e.response?.data?.detail || "Could not activate";
      toast.error(msg);
      if (e.response?.status === 402) onTopUp?.();
    } finally { setBusy(false); }
  };

  const draftInbox = async () => {
    setBusy(true);
    try { const r = await api.assistantRun(); toast.success(r.message); setRun({ status: "running", total: r.total, done: 0, drafted: 0 }); startPoll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start"); setBusy(false); }
  };

  const approve = async (d) => {
    try { const r = await api.assistantApprove(d.id, edits[d.id] ?? d.draft); toast.success(r.message || "Sent"); setDrafts((p) => p.filter((x) => x.id !== d.id)); loadStatus(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };
  const dismiss = async (d) => {
    try { await api.assistantDismiss(d.id); setDrafts((p) => p.filter((x) => x.id !== d.id)); }
    catch { toast.error("Could not skip"); }
  };
  const approveAll = async () => {
    if (!window.confirm(`Send all ${drafts.length} drafted replies from your Mac?`)) return;
    setBusy(true);
    const payload = {}; drafts.forEach((d) => { payload[d.id] = edits[d.id] ?? d.draft; });
    try { const r = await api.assistantApproveAll(payload); toast.success(r.message); setDrafts([]); loadStatus(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send all"); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="flex items-center gap-2 text-zinc-400 text-sm font-mono py-16 justify-center"><Loader2 size={16} className="animate-spin" /> Loading your assistant…</div>;

  const running = run?.status === "running";
  const onRenamed = (pid, name) => setDrafts((p) => p.map((x) => (x.person_id === pid ? { ...x, name } : x)));
  const q = search.trim().toLowerCase();
  const shown = q ? drafts.filter((d) => [(d.name || ""), (d.handle || ""), (d.incoming || "")].some((v) => v.toLowerCase().includes(q))) : drafts;

  return (
    <div className="space-y-4" data-testid="assistant-desk">
      {/* Header */}
      <div className="rounded-xl border border-fuchsia-400/20 bg-gradient-to-br from-fuchsia-500/[0.07] to-cyan-500/[0.04] p-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl grid place-items-center bg-fuchsia-500/15 border border-fuchsia-400/30 shrink-0"><Bot size={20} className="text-fuchsia-300" /></div>
          <div>
            <div className="text-zinc-100 font-tactical text-base flex items-center gap-2">{status.name || "Your Assistant"}
              {status.seat_active ? <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">{status.tier_label} · active</span>
                : <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-zinc-500/15 text-zinc-400 border border-white/10">not hired</span>}
            </div>
            <div className="text-[12px] text-zinc-400 mt-0.5 max-w-md">{status.tone ? status.tone : "A personal assistant that drafts replies across your whole inbox — you review, tweak, and send."}</div>
          </div>
        </div>
        <button data-testid="assistant-settings-btn" onClick={() => setShowConfig((v) => !v)} className="text-xs font-tactical uppercase tracking-wider px-3 py-1.5 rounded-lg border border-white/10 text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1.5 shrink-0"><Settings2 size={13} /> Settings</button>
      </div>

      {/* Config form */}
      {showConfig && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="assistant-config">
          <div className="text-cyan-200 font-tactical uppercase tracking-wider text-sm mb-3 flex items-center gap-2"><Wand2 size={14} /> Shape your assistant</div>
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Name" hint="What you'll call this assistant.">
              <input data-testid="assistant-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ava" />
            </Field>
            <Field label="Signature (optional)" hint="Appended to every sent reply.">
              <input data-testid="assistant-signature" className={inputCls} value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} placeholder="– sent by Ava for Taylor" />
            </Field>
          </div>
          <Field label="Voice & tone" hint="How should it sound? It also learns from your real sent texts.">
            <textarea data-testid="assistant-tone" className={`${inputCls} h-20 resize-none`} value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} placeholder="Warm, upbeat, concise. Casual like how I text. Use my name when it fits." />
          </Field>
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Always do"><textarea data-testid="assistant-dos" className={`${inputCls} h-16 resize-none`} value={form.dos} onChange={(e) => setForm({ ...form, dos: e.target.value })} placeholder="Confirm times, offer to hop on a call, be helpful." /></Field>
            <Field label="Never do"><textarea data-testid="assistant-donts" className={`${inputCls} h-16 resize-none`} value={form.donts} onChange={(e) => setForm({ ...form, donts: e.target.value })} placeholder="Never promise money, never share my address, no emojis." /></Field>
          </div>
          <button data-testid="assistant-save" onClick={save} disabled={busy} className="mt-1 text-sm font-tactical uppercase tracking-wider px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 disabled:opacity-50 inline-flex items-center gap-2">{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save assistant</button>
        </div>
      )}

      {/* Plans (if configured but no seat) */}
      {status.configured && !status.seat_active && (
        <div data-testid="assistant-plans">
          <div className="text-zinc-300 text-sm font-tactical uppercase tracking-wider mb-2">Hire your assistant</div>
          <div className="grid sm:grid-cols-3 gap-3">
            {tiers.map((t) => (
              <div key={t.key} data-testid={`assistant-tier-${t.key}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex flex-col">
                <div className="text-zinc-100 font-tactical text-base">{t.label}</div>
                <div className="text-2xl font-tactical font-bold text-cyan-300 mt-1">{t.credits}<span className="text-xs text-zinc-500 font-mono"> credits</span></div>
                <div className="text-[10px] text-zinc-500 font-mono">≈ ${t.usd}/mo · up to {t.max_contacts.toLocaleString()} contacts</div>
                <div className="text-[12px] text-zinc-400 mt-2 flex-1">{t.blurb}</div>
                <button data-testid={`assistant-activate-${t.key}`} onClick={() => activate(t.key)} disabled={busy} className="mt-3 text-sm font-tactical uppercase tracking-wider px-3 py-2 rounded-lg bg-fuchsia-500/20 border border-fuchsia-400/40 text-fuchsia-100 hover:bg-fuchsia-500/30 disabled:opacity-50 inline-flex items-center justify-center gap-2"><Zap size={13} /> Activate</button>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-zinc-500 mt-2">Flat monthly seat, paid in credits. Drafting is included — you always review before anything sends.</div>
        </div>
      )}

      {/* Active seat: draft + queue */}
      {status.seat_active && (
        <>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-zinc-200 text-sm font-semibold flex items-center gap-2"><Sparkles size={14} className="text-fuchsia-300" /> {status.candidates} conversation{status.candidates === 1 ? "" : "s"} waiting on a reply</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">Your assistant drafts a reply for each one. Review and send below.</div>
            </div>
            <button data-testid="assistant-draft-inbox" onClick={draftInbox} disabled={busy || running || status.candidates === 0} className="text-sm font-tactical uppercase tracking-wider px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/30 disabled:opacity-50 inline-flex items-center gap-2">
              {running ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} {running ? "Drafting…" : "Draft my inbox"}
            </button>
          </div>

          {running && (
            <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/[0.05] p-3" data-testid="assistant-run-progress">
              <div className="flex items-center justify-between text-[12px] mb-1.5"><span className="text-cyan-200 font-semibold">Drafting replies…</span><span className="font-mono text-cyan-200/80">{run.done}/{run.total}</span></div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-cyan-400 rounded-full transition-all duration-500" style={{ width: `${run.total ? Math.round((run.done / run.total) * 100) : 0}%` }} /></div>
            </div>
          )}

          {drafts.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-zinc-400 text-xs font-mono">{drafts.length} draft{drafts.length === 1 ? "" : "s"} · newest first</div>
                <button data-testid="assistant-approve-all" onClick={approveAll} disabled={busy} className="text-sm font-tactical uppercase tracking-wider px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-50 inline-flex items-center gap-2"><CheckCheck size={14} /> Approve all ({drafts.length})</button>
              </div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input data-testid="assistant-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, number, or what they said…" className={`${inputCls} pl-9`} />
              </div>
            </div>
          )}

          <div className="space-y-3">
            {drafts.length === 0 && !running ? (
              <div className="text-center text-zinc-600 text-sm py-10">No drafts yet. Hit "Draft my inbox" to let your assistant get to work.</div>
            ) : shown.length === 0 ? (
              <div className="text-center text-zinc-600 text-sm py-8">No drafts match “{search}”.</div>
            ) : shown.map((d) => (
              <DraftCard key={d.id} d={d} value={edits[d.id] ?? d.draft}
                onChange={(v) => setEdits({ ...edits, [d.id]: v })}
                onApprove={() => approve(d)} onSkip={() => dismiss(d)} onRenamed={onRenamed} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
